/**
 * features/exams/ExamsHub.tsx — Exams, the section that RUNS an exam series.
 * ------------------------------------------------------------------
 * A pupil's Exams tab answers "what do I sit and when". This answers the
 * opposite question, the one the office and the invigilators have: who is in
 * the building on Wednesday morning, how many scripts per paper, who is on
 * duty, and who turned up.
 *
 * ONE SCREEN PER NAV ENTRY, not a tab rail inside one screen (the school,
 * 2026-09-18). Exams is its own sidebar GROUP:
 *   Exam days    the session about to be run, and what is coming
 *   Timetable    the dates, open to every teacher in the division
 *   Invigilation the rota, session by session, with the lessons a duty would
 *                empty and the load each person is carrying
 *   My duties    one teacher's own duties — a separate screen, not a filter
 *   Registers    who should be in the room, and who was — filled in the hall
 *   Seating      the rooms, and a plan per session with desk labels
 *   Registration the candidate file for Cambridge Direct (ExamRegistration)
 *   Candidates   the set-up screen (which classes, which papers, publish) —
 *                MOVED here from Academic Admin, same screen, same gate
 *
 * So every screen has its own address (`#/exams`, `#/examTimetable`,
 * `#/examTimetables`), which a tab inside one screen never has, and the
 * sidebar shows a teacher exactly the exam screens they may open.
 *
 * Rooms and seating came last, by the school's choice on 2026-09-18: they
 * needed a room list with desk counts and a layout, which nothing in the
 * portal held — Phase 4 adds `reportExamRooms` for exactly that.
 *
 * THE TWO ROTA SCREENS READ THEIR OWN DATA, not `useExamRun` below: duties,
 * the school timetable and the bell times, none of which the candidate
 * screens need, and 113 statements of entry, which the rota does not.
 *
 * EVERY SERIES, CHOSEN AT THE TOP (2026-10-29). The school's own exams are a
 * second `kind` of series (`internal`, uploaded on Exams → Internal exams), in
 * the same shapes, so every pane works on whichever series is picked here
 * (remembered per device in `lkc.exams.series`). Cambridge stays the default.
 *
 * TWO GATES, not one. `canViewExams` opens the section — every teacher needs
 * the dates and (soon) their own duties. `canManageExamTimetables` is what
 * lets anyone change a series, and the Candidates tab is hidden without it.
 */
import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, ClipboardCheck, ClipboardList, Grid3x3, ShieldCheck, UserCheck, Wrench } from 'lucide-react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Page, PageHeader, SoftCard, glassFieldClass } from '@/components/ui/design';
import {
  compareClassNames,
  nextSessionLoad,
  sessionLoads,
  type CambridgeTimetable,
  type CandidateEntry,
  type ExamSeries,
  type ExamSessionLoad,
  type PupilExamEntries,
} from '@/lib/examTimetable';
import { EMPTY_TIMETABLE, loadTimetable, useMinuteClock } from './useExamTimetable';
import ExamTimetablesAdmin from './ExamTimetablesAdmin';
import ExamDaysPane from './ExamDaysPane';
import ExamTimetablePane from './ExamTimetablePane';
import InternalExams from './InternalExams';
import ExamRegistration from './ExamRegistration';
import ExamTools from './ExamTools';
import InvigilationPane from './InvigilationPane';
import MyDutiesPane from './MyDutiesPane';
import ExamRegistersPane from './ExamRegistersPane';
import ExamSeatingPane from './ExamSeatingPane';

/** The series the section opens on until one is picked. */
/* LKC (30 Oct 2026): the End of Year exams are the first series it runs here. */
const DEFAULT_SERIES_ID = 'lkc-eoy-2026';
const SERIES_KEY = 'lkc.exams.series';

export interface SeriesChoice { id: string; title: string; kind: ExamSeries['kind']; published: boolean }

/** Every series, and the one chosen on this device. */
export function useSeriesChoice(): { list: SeriesChoice[]; id: string; title: string; choose: (id: string) => void } {
  const [list, setList] = useState<SeriesChoice[]>([]);
  const [id, setId] = useState<string>(() => {
    try { return localStorage.getItem(SERIES_KEY) || DEFAULT_SERIES_ID; } catch { return DEFAULT_SERIES_ID; }
  });
  useEffect(() => {
    void getDocs(collection(db, 'reportExamSeries')).then((snap) => setList(snap.docs.map((d) => {
      const x = d.data() as ExamSeries;
      return { id: d.id, title: x.title || d.id, kind: x.kind ?? 'cambridge', published: !!x.published };
    }).sort((a, b) => (a.kind === b.kind ? a.title.localeCompare(b.title) : a.kind === 'cambridge' ? -1 : 1)))).catch(() => setList([]));
  }, []);
  const known = list.length && !list.some((s) => s.id === id) ? DEFAULT_SERIES_ID : id;
  return {
    list, id: known, title: list.find((s) => s.id === known)?.title ?? 'End of Year Examinations, November 2026',
    choose: (next) => { setId(next); try { localStorage.setItem(SERIES_KEY, next); } catch { /* a convenience only */ } },
  };
}

/** The picker, in each pane's header. Hidden while there is only one series. */
export function SeriesPicker({ choice }: { choice: ReturnType<typeof useSeriesChoice> }) {
  if (choice.list.length < 2) return null;
  return (
    <label className="text-xs text-[var(--sp-muted)]">
      <span className="sr-only">Exam series</span>
      <select value={choice.id} onChange={(e) => choice.choose(e.target.value)} className={glassFieldClass('!w-auto')}>
        {choice.list.map((s) => (
          <option key={s.id} value={s.id}>{s.title}{s.kind === 'internal' ? ' (internal)' : ''}{s.published ? '' : ' (draft)'}</option>
        ))}
      </select>
    </label>
  );
}

/* LKC (30 Oct 2026): every pane of Al-Nur's, plus `internal` (the End of Year
   timetable, loaded from the Examinations Unit's PDF) and `tools` (LKC's class
   subject lists). The exam room screen is its own route (App.tsx). */
export type ExamPane = 'days' | 'timetable' | 'invigilation' | 'myDuties' | 'registers' | 'seating'
  | 'candidates' | 'internal' | 'tools' | 'registration';

export interface ExamRunData {
  series: ExamSeries | null;
  timetable: CambridgeTimetable | null;
  candidates: CandidateEntry[];
  loads: ExamSessionLoad[];
  loading: boolean;
}

/**
 * The series, the Cambridge timetable and every candidate's entries — read
 * ONCE for the whole section. Each tab counts the same rows differently, and
 * three tabs each fetching 113 statements would be three times the wait for
 * the same answer.
 */
function useExamRun(seriesId: string): ExamRunData {
  const { students } = useData();
  const [series, setSeries] = useState<ExamSeries | null>(null);
  const [timetable, setTimetable] = useState<CambridgeTimetable | null>(null);
  const [entries, setEntries] = useState<PupilExamEntries[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setTimetable(null);
    void (async () => {
      try {
        const [seriesSnap, entrySnap] = await Promise.all([
          getDoc(doc(db, 'reportExamSeries', seriesId)),
          getDocs(query(collection(db, 'reportExamEntries'), where('seriesId', '==', seriesId))),
        ]);
        if (!live) return;
        const found = seriesSnap.exists() ? (seriesSnap.data() as ExamSeries) : undefined;
        setSeries(found ?? null);
        setEntries(entrySnap.docs.map((d) => d.data() as PupilExamEntries));
        if (found?.kind === 'internal') setTimetable(EMPTY_TIMETABLE);
        else if (found?.timetableId) {
          const t = await loadTimetable(found.timetableId);
          if (live) setTimetable(t);
        }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [seriesId]);

  /* A candidate is a pupil's statement plus the class they sit in — the class
     comes from the pupil record, because a statement names a centre candidate
     and knows nothing about Form 5A. */
  const candidates = useMemo<CandidateEntry[]>(() => {
    const classOf = new Map((students ?? []).map((s) => [s.id, s.currentClass || '']));
    const names = new Map((series?.classes ?? []).map((c) => [c.className, c.className]));
    return entries.map((e) => {
      const raw = classOf.get(e.studentId) || '';
      return {
        studentId: e.studentId,
        candidateName: e.candidateName,
        candidateNumber: e.candidateNumber,
        className: names.get(raw) ?? raw,
        components: e.components ?? [],
      };
    }).sort((a, b) => compareClassNames(a.className, b.className)
      || a.candidateNumber.localeCompare(b.candidateNumber));
  }, [entries, students, series]);

  const loads = useMemo(
    () => sessionLoads(candidates, timetable, series?.sessions, series?.paperTimes),
    [candidates, timetable, series],
  );

  return { series, timetable, candidates, loads, loading };
}

/**
 * The two screens that are the EXAM OFFICER'S WORK, not a teacher's
 * reference (the school, 2026-09-19). A teacher needs the dates, their own
 * duties, the registers they take and where the desks are; running the
 * series — which session is next, how many scripts, who is on the rota — is
 * one person's job, so those screens sit behind the gate that can change a
 * series. Candidates guards itself inside ExamTimetablesAdmin.
 *
 * Gating the SIDEBAR alone would only hide a link: `#/exams` typed into the
 * address bar would still open it, which is exactly the hole ReportGeneration
 * and BulkSend had.
 */
const OFFICE_ONLY: ExamPane[] = ['days', 'invigilation'];

export default function ExamsHub({ pane = 'days', openId, year, term }: { pane?: ExamPane; openId?: string; year?: string; term?: string }) {
  const { permissions } = useAuth();

  if (OFFICE_ONLY.includes(pane) && !permissions.canManageExamTimetables) {
    return <Page wide><Refused pane={pane} /></Page>;
  }
  /* Candidates and Internal exams are set-up screens whole, and draw their own
     page frame and header. Tools is LKC's earlier exam screen, unchanged. */
  if (pane === 'candidates') return <ExamTimetablesAdmin />;
  if (pane === 'internal') return <InternalExams />;
  if (pane === 'registration') return <ExamRegistration />;
  if (pane === 'tools') return <ExamTools year={year ?? ''} term={term ?? ''} />;
  if (pane === 'invigilation' || pane === 'myDuties' || pane === 'registers' || pane === 'seating') {
    return <Page wide><Rota pane={pane} openId={openId} /></Page>;
  }
  return <Page wide><Body pane={pane} /></Page>;
}

type RunPane = 'days' | 'timetable' | 'invigilation' | 'myDuties' | 'registers' | 'seating';
const TITLES: Record<RunPane, { title: string; icon: typeof ClipboardList }> = {
  days: { title: 'Exam days', icon: ClipboardList },
  timetable: { title: 'Exam timetable', icon: CalendarClock },
  invigilation: { title: 'Invigilation', icon: ShieldCheck },
  myDuties: { title: 'My duties', icon: UserCheck },
  registers: { title: 'Exam registers', icon: ClipboardCheck },
  seating: { title: 'Rooms and seating', icon: Grid3x3 },
};

/** The rota and register screens: one header, and no candidate-load read
 *  behind it — they need the duties, not the whole series. */
const ROTA_SUBTITLE: Record<'invigilation' | 'myDuties' | 'registers' | 'seating', string> = {
  invigilation: 'Who is in each room, what it costs the timetable, and who has had how much',
  myDuties: 'Your own exam duties, and the lessons they fall on',
  registers: 'Who should be in the room, and who was — taken by the invigilator, kept by the office',
  seating: 'The rooms the school uses, and who sits at which desk',
};

function Rota({ pane, openId }: { pane: 'invigilation' | 'myDuties' | 'registers' | 'seating'; openId?: string }) {
  const now = useMinuteClock();
  const { title, icon } = TITLES[pane];
  const choice = useSeriesChoice();
  const id = choice.id;

  return (
    <>
      <PageHeader icon={icon} eyebrow={choice.title} title={title} subtitle={ROTA_SUBTITLE[pane]} actions={<SeriesPicker choice={choice} />} />
      {pane === 'invigilation' && <InvigilationPane key={id} seriesId={id} />}
      {pane === 'myDuties' && <MyDutiesPane key={id} seriesId={id} now={now} />}
      {pane === 'registers' && <ExamRegistersPane key={id} seriesId={id} now={now} openId={openId} />}
      {pane === 'seating' && <ExamSeatingPane key={id} seriesId={id} seriesTitle={choice.title} />}
    </>
  );
}

function Body({ pane }: { pane: 'days' | 'timetable' }) {
  const choice = useSeriesChoice();
  const run = useExamRun(choice.id);
  const now = useMinuteClock();
  const next = useMemo(() => nextSessionLoad(run.loads, now), [run.loads, now]);
  const { title, icon } = TITLES[pane];

  return (
    <>
      <PageHeader
        icon={icon}
        eyebrow={run.series?.title ?? 'Exams'}
        title={title}
        subtitle={run.loading
          ? 'Loading the series…'
          : `${run.candidates.length} candidates · ${run.loads.length} sessions${
            next ? ` · next ${shortDay(next.date)}${next.session ? ` ${next.session}` : ''}` : ''}`}
        actions={<SeriesPicker choice={choice} />}
      />

      {pane === 'days' && <ExamDaysPane run={run} now={now} />}
      {pane === 'timetable' && <ExamTimetablePane run={run} now={now} />}
    </>
  );
}

/** Not a dead end: it names the screen that answers the same question for a
 *  teacher, which is the difference between a refusal and a wall. */
function Refused({ pane }: { pane: ExamPane }) {
  const { title, icon } = TITLES[pane as RunPane];
  return (
    <>
      <PageHeader icon={icon ?? Wrench} eyebrow="Exams" title={title} subtitle="Kept by the exam officer" />
      <SoftCard>
        <p className="text-sm text-[var(--sp-body)]">
          {pane === 'invigilation'
            ? 'The invigilation rota is kept by the exam officer. '
            : 'Running the exam days is the exam officer’s job. '}
          You can see the{' '}
          <a href="#/examTimetable" className="font-semibold text-primary hover:underline">exam timetable</a>,{' '}
          <a href="#/myInvigilation" className="font-semibold text-primary hover:underline">your duties</a>,{' '}
          <a href="#/examRegisters" className="font-semibold text-primary hover:underline">registers</a> and{' '}
          <a href="#/examSeating" className="font-semibold text-primary hover:underline">rooms &amp; seating</a>.
        </p>
      </SoftCard>
    </>
  );
}

/** "Wed 7 Oct" — the way the office says a date out loud. */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d))
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}
